import { motion, AnimatePresence } from 'framer-motion'
import {
  Phone,
  MessageCircle,
  CalendarCheck,
  CalendarClock,
  Sparkles,
} from 'lucide-react'
import type { BdrLead, Cadencia, BdrQueueGroups } from '@/types'
import { BdrQueueEmptyGroup, BdrQueueGroupHeader } from '@/components/BdrPanelV2/BdrQueueGroupHeader'
import { BdrQueueActionBtn, BdrQueueLeadCard } from '@/components/BdrPanelV2/BdrQueueLeadCard'

interface BdrQueueProps {
  queueGroups: BdrQueueGroups
  collapsed: Record<string, boolean>
  toggleGroup: (key: string) => void
  openLeadDetail: (lead: BdrLead, isTurbo?: boolean, channel?: string) => void
}

export function BdrQueue({ queueGroups, collapsed, toggleGroup, openLeadDetail }: BdrQueueProps) {
  const groups: {
    key: string
    label: string
    color: string
    icon: React.ElementType
    items: { lead: BdrLead; cad: Cadencia | null; horario?: { label: string; color: string } }[]
    emptyMsg: string
    renderCard: (
      lead: BdrLead,
      cad: Cadencia | null,
      horario?: { label: string; color: string },
    ) => React.ReactNode
  }[] = [
    {
      key: 'ligacoes',
      label: 'Contatos do Dia (Prioridade)',
      color: '#3B82F6',
      icon: Phone,
      items: queueGroups.ligacoes,
      emptyMsg: 'Nenhum contato prioritario hoje.',
      renderCard: (lead, cad, horario) => (
        <BdrQueueLeadCard
          lead={lead}
          cad={cad}
          horario={horario}
          showTentativa
          showHorario
          onOpen={() => openLeadDetail(lead)}
          actions={
            <>
              <BdrQueueActionBtn
                icon={Phone}
                color="#3B82F6"
                title="Ligar FX"
                onClick={() => openLeadDetail(lead, true, 'WhatsApp')}
              />
              <BdrQueueActionBtn
                icon={MessageCircle}
                color="#10B981"
                title="Whats FX"
                onClick={() => openLeadDetail(lead, true, 'WhatsApp')}
              />
            </>
          }
        />
      ),
    },
    {
      key: 'followup',
      label: 'Follow-ups & Mensagens',
      color: '#10B981',
      icon: MessageCircle,
      items: queueGroups.followup,
      emptyMsg: 'Nenhum follow-up pendente.',
      renderCard: (lead, cad, horario) => (
        <BdrQueueLeadCard
          lead={lead}
          cad={cad}
          horario={horario}
          showTentativa
          showFollowUp
          onOpen={() => openLeadDetail(lead)}
          actions={
            <BdrQueueActionBtn
              icon={MessageCircle}
              color="#10B981"
              title="Enviar Mensagem FX"
              onClick={() => openLeadDetail(lead, true, 'WhatsApp')}
            />
          }
        />
      ),
    },
    {
      key: 'reunioes',
      label: 'Reunioes Agendadas',
      color: '#F59E0B',
      icon: CalendarCheck,
      items: queueGroups.reunioes,
      emptyMsg: 'Nenhuma reuniao agendada.',
      renderCard: (lead, cad, horario) => (
        <BdrQueueLeadCard
          lead={lead}
          cad={cad}
          horario={horario}
          showAgendado
          onOpen={() => openLeadDetail(lead)}
          actions={
            <BdrQueueActionBtn
              icon={Sparkles}
              color="#F59E0B"
              title="Ver Detalhes"
              onClick={() => openLeadDetail(lead)}
            />
          }
        />
      ),
    },
    {
      key: 'planejamento',
      label: 'Planejamento (Proximos Passos)',
      color: '#8B5CF6',
      icon: CalendarClock,
      items: queueGroups.planejamento,
      emptyMsg: 'Nenhum lead em planejamento.',
      renderCard: (lead, cad, horario) => (
        <BdrQueueLeadCard
          lead={lead}
          cad={cad}
          horario={horario}
          showTentativa
          showAmanha
          onOpen={() => openLeadDetail(lead)}
        />
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => {
        const isCollapsed = collapsed[group.key] ?? false

        return (
          <div key={group.key} className="glass-card border-white/[0.06] overflow-hidden">
            <BdrQueueGroupHeader
              label={group.label}
              count={group.items.length}
              color={group.color}
              icon={group.icon}
              collapsed={isCollapsed}
              onToggle={() => toggleGroup(group.key)}
            />

            <AnimatePresence initial={false}>
              {!isCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4">
                    {group.items.length === 0 ? (
                      <BdrQueueEmptyGroup message={group.emptyMsg} />
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
                          {group.items.slice(0, 50).map(({ lead, cad, horario }) => (
                            <div key={lead.id}>{group.renderCard(lead, cad, horario)}</div>
                          ))}
                        </div>
                        {group.items.length > 50 && (
                          <div className="mt-4 flex justify-center">
                            <button className="text-xs font-bold text-accent hover:text-accent-light transition-colors bg-accent/10 hover:bg-accent/20 px-4 py-2.5 rounded-lg border border-accent/20">
                              Há mais {group.items.length - 50} leads nesta fila. Use a aba "Todos os
                              Leads" para ver todos.
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}
